import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faSpinner, faCheck, faUndo } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch, formatDate } from '../../utils/http';

interface Entry {
  id: string;
  studentId: string;
  studentName: string;
  studentIdNumber: string;
  entryDate: string;
  hoursWorked: number;
  description: string;
  mentorName: string | null;
  status: number; // 0 Submitted, 1 Approved, 2 Returned
  feedback: string | null;
}

interface Summary {
  studentId: string;
  studentName: string;
  studentIdNumber: string;
  entryCount: number;
  totalHours: number;
  approvedHours: number;
  pendingReview: number;
}

interface CourseData {
  canReview: boolean;
  summaries: Summary[];
  entries: Entry[];
}

const STATUS_LABEL = ['Pending review', 'Approved', 'Returned'];
const STATUS_BADGE = ['bg-amber-100 text-amber-700', 'bg-green-100 text-green-700', 'bg-red-100 text-red-700'];

export default function WorkplaceStaffPanel({ learnershipId }: { learnershipId: string }) {
  const { accessToken } = useAuth();
  const [data, setData] = useState<CourseData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<string>('all');
  const [feedbackById, setFeedbackById] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const res = await authFetch<CourseData>(accessToken, `/api/workplace/course/${learnershipId}`);
    if (res.ok && res.data) {
      setData(res.data);
      setError('');
    } else {
      setError(res.message || 'Failed to load workplace logbook.');
    }
    setIsLoading(false);
  }, [accessToken, learnershipId]);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (id: string, approve: boolean) => {
    setBusyId(id);
    const res = await authFetch(accessToken, `/api/workplace/${id}/review`, {
      method: 'POST',
      body: { approve, feedback: feedbackById[id] ?? '' },
    });
    setBusyId(null);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setError('');
    load();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>;
  }

  const entries = data.entries.filter((e) => filter === 'all' || e.studentId === filter);

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

      {data.summaries.length === 0 ? (
        <div className="card-surface p-12 text-center">
          <FontAwesomeIcon icon={faBriefcase} className="h-12 w-12 text-brand-300 mb-4" />
          <p className="text-brand-900/60">No workplace logbook entries have been captured yet.</p>
        </div>
      ) : (
        <>
          <div className="card-surface overflow-hidden">
            <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-4 px-5 py-3 bg-canvas-50 text-xs font-bold uppercase tracking-wider text-brand-900/50">
              <span>Learner</span>
              <span>Entries</span>
              <span>Total hours</span>
              <span>Approved hours</span>
              <span>Pending</span>
            </div>
            <div className="divide-y divide-canvas-200">
              {data.summaries.map((s) => (
                <button
                  key={s.studentId}
                  onClick={() => setFilter(filter === s.studentId ? 'all' : s.studentId)}
                  className={`w-full text-left grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-2 md:gap-4 px-5 py-3 items-center transition-colors ${
                    filter === s.studentId ? 'bg-brand-50' : 'hover:bg-canvas-50'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-950 truncate">{s.studentName}</p>
                    <p className="text-xs text-brand-900/50">{s.studentIdNumber}</p>
                  </div>
                  <span className="text-sm text-brand-900/70">{s.entryCount}</span>
                  <span className="text-sm text-brand-900/70">{s.totalHours} h</span>
                  <span className="text-sm text-brand-900/70">{s.approvedHours} h</span>
                  <span className="text-sm text-brand-900/70">{s.pendingReview}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {entries.map((e) => (
              <div key={e.id} className="card-surface p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-950">
                      {e.studentName} · {formatDate(e.entryDate)} · {e.hoursWorked} h
                    </p>
                    {e.mentorName && <p className="text-xs text-brand-900/50">Mentor: {e.mentorName}</p>}
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${STATUS_BADGE[e.status]}`}>{STATUS_LABEL[e.status]}</span>
                </div>
                <p className="text-sm text-brand-900/80 mt-3 whitespace-pre-wrap">{e.description}</p>
                {e.feedback && (
                  <p className="text-xs text-brand-900/70 mt-3 bg-canvas-50 border border-canvas-200 rounded-lg p-3">
                    <span className="font-semibold">Feedback:</span> {e.feedback}
                  </p>
                )}
                {data.canReview && e.status !== 1 && (
                  <div className="mt-4 space-y-2">
                    <input
                      type="text"
                      value={feedbackById[e.id] ?? ''}
                      onChange={(ev) => setFeedbackById((prev) => ({ ...prev, [e.id]: ev.target.value }))}
                      placeholder="Feedback (required when returning)"
                      className="w-full px-3 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => review(e.id, true)}
                        disabled={busyId === e.id}
                        className="px-3 py-1.5 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-1.5"
                      >
                        <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                        Approve
                      </button>
                      <button
                        onClick={() => review(e.id, false)}
                        disabled={busyId === e.id}
                        className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                      >
                        <FontAwesomeIcon icon={faUndo} className="h-3 w-3" />
                        Return for correction
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
